import React, { useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import Seo from '@/components/common/Seo';
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CallToAction, LogoCarousel } from '@/components/ui/content-blocks';
import HeroSection from './HeroSection';
import { Button } from '@/components/ui/button';
import { getImageAssetPath } from '@/lib/imageHelper';
import { getCanonicalPath } from '@/lib/seoHelper';

// Registriere die Übersetzungen
import translations from './translations';
import { registerTranslations } from '@/hooks/useTranslation';

// Registriere die Übersetzungen für diese Seite
registerTranslations('homePage', translations);

const HomePage = () => {
  const { t } = useTranslation();
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo 
        title={t('navigation.home')}
        description={t('homePage.hero.description')}
        canonical={getCanonicalPath('/')}
        og={{
          type: 'website',
          image: '/images/og-image-home.jpg',
        }}
      />
      <Layout>
        <main>
          {/* Hero-Sektion */}
          <HeroSection />

          {/* Hauptdienstleistungen */}
          <section id="services" className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t('homePage.services.title')}</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('homePage.services.description')}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: t('homePage.services.consulting.title'),
                    description: t('homePage.services.consulting.description'),
                    icon: "🔍",
                    link: "/consulting"
                  },
                  {
                    title: t('homePage.services.erp.title'),
                    description: t('homePage.services.erp.description'),
                    icon: "💼",
                    link: "/erp"
                  },
                  {
                    title: t('homePage.services.shopware.title'),
                    description: t('homePage.services.shopware.description'),
                    icon: "🛒",
                    link: "/shopware"
                  },
                  {
                    title: t('homePage.services.businessApps.title'),
                    description: t('homePage.services.businessApps.description'),
                    icon: "📱",
                    link: "/business-apps"
                  },
                  {
                    title: t('homePage.services.interfaces.title'),
                    description: t('homePage.services.interfaces.description'),
                    icon: "🔄",
                    link: "/interfaces"
                  },
                  {
                    title: t('homePage.services.ctoService.title'),
                    description: t('homePage.services.ctoService.description'),
                    icon: "👨‍💻",
                    link: null
                  }
                ].map((service, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md flex flex-col h-full">
                    <div className="text-4xl mb-4">{service.icon}</div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600 flex-grow mb-4">{service.description}</p>
                    {service.link && (
                      <Link to={service.link} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                        {t('homePage.services.learnMore')} <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Referenz-Logos */}
          {/* <section className="py-16">
            <div className="container mx-auto">
              <LogoCarousel 
                title={t('homePage.references.title')}
                description={t('homePage.references.description')}
              />
            </div>
          </section> */}

          {/* Allgemeiner Call-to-Action */}
          <CallToAction
            title={t('homePage.generalCta.title')}
            description={t('homePage.generalCta.description')}
            cta={{
              label: t('homePage.generalCta.cta'),
              href: '/about'
            }}
          />
        </main>
      </Layout>
    </>
  );
};

export default HomePage; 